import { Eye, EyeOff, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { createSourceSchema, customSourceKey, type AdminSourceView, type SourceKind } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { ARCHIVED_TONE } from '../../../components/status-tones';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip } from '../../../components/Tone';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useCanEdit, useCanView } from '../../../stores/session';
import { SOURCE_ROWS, sourcesApi, useSources } from '../api/sources-api';
import { ListTable, type ListColumn } from '../components/simple-list/ListTable';
import { FilterPills, ListNoMatch, ListSearch, ListToolbar, PanelActions, PanelFacts, PanelSection, SidePanel, SimpleListPage, usePanelMode } from '../components/simple-list/SimpleList';
import { useListAddress } from '../components/simple-list/use-list-address';
import { isPendingRow, pendingId, useRowChanges, type RowChanges } from '../components/simple-list/use-row-changes';

const PATH = '/admin/sources';
const ADDING = 'new';
const FILTERS = ['shown', 'hidden', 'added'] as const;
type SourceFilter = (typeof FILTERS)[number];
const KIND_LABELS: Readonly<Record<SourceKind, string>> = { built_in: 'Built in', custom: 'Added', system: 'Set by the system' };

/**
 * ADMIN-08 / F19.08 — where candidates come from (DECISIONS-LOG S29–S32 Q18), in the simple-list pattern (Admin redesign
 * batch 2, option A): every source with its applications and "Hired from it", and the picked one in the panel to
 * rename, hide or show, or switch "Jobs can be published to it" — each at once with Undo. Built-in sources are never
 * deleted, because past applications and every source report point at them. The Career site row shows only while that
 * part is on (#13). Admin and HR (`hiring_setup`).
 */
export function SourcesPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const isOffersOn = useModule('offers');
  const sources = useSources(canView);
  const changes = useRowChanges(SOURCE_ROWS);
  const mode = usePanelMode();
  const [address, setAddress] = useListAddress(PATH);
  const filter = FILTERS.find((value) => value === address.show) ?? null;
  const query = address.q ?? '';

  if (!canView) return <ForbiddenState resource="the source list" contact="Your admin" />;
  if (sources.isPending) return <SkeletonLines count={10} />;
  if (sources.error && !sources.data) return <ServerErrorState onRetry={() => void sources.refetch()} />;

  const all = sources.data;
  const shown = all.filter((row) => matchesFilter(row, filter) && matchesQuery(row, query));
  const isAdding = address.open === ADDING && canEdit;
  const open = all.find((row) => row.key === address.open) ?? null;
  const close = (): void => setAddress({ open: undefined });
  const hiredHeader = isOffersOn ? 'Hired from it' : 'Selected from it';

  const columns: ListColumn<AdminSourceView>[] = [
    { key: 'kind', header: 'Kind', isWideOnly: true, render: (row) => KIND_LABELS[row.kind] },
    { key: 'board', header: 'Job board', isWideOnly: true, render: boardText },
    { key: 'applications', header: 'Applications', isNumeric: true, render: (row) => formatNumber(row.applications) },
    { key: 'hired', header: hiredHeader, isNumeric: true, isWideOnly: true, render: (row) => formatNumber(row.hired) },
  ];

  return (
    <SimpleListPage
      crumb="Admin · Hiring setup"
      title="Sources"
      total={all.length}
      lede="Where candidates come from — offered when a job is published, when someone is added by hand, and on every source report. Built-in sources can be renamed or hidden from the pickers but never deleted, because past applications and every source report point at them."
      actions={
        canEdit && (
          <Button variant="solid" icon={Plus} onClick={() => setAddress({ open: ADDING })}>
            Add a source
          </Button>
        )
      }
      toolbar={
        <ListToolbar search={<ListSearch label="Find a source" value={query} onChange={(next) => setAddress({ q: next })} />}>
          <FilterPills
            label="Show sources that are"
            value={filter}
            options={[
              { value: 'shown', label: 'Shown', count: all.filter((row) => !row.isHidden).length },
              { value: 'hidden', label: 'Hidden', count: all.filter((row) => row.isHidden).length },
              { value: 'added', label: 'Added', count: all.filter((row) => row.kind === 'custom').length },
            ]}
            onChange={(next) => setAddress({ show: next ?? undefined })}
          />
        </ListToolbar>
      }
      panel={
        isAdding ? (
          <SidePanel mode={mode} label="Add a source" title="Add a source" onClose={close}>
            <AddSource changes={changes} onAdded={(key) => setAddress({ open: key, show: undefined, q: undefined })} />
          </SidePanel>
        ) : (
          open && (
            <SidePanel mode={mode} label={`Source: ${open.label}`} title={open.label} sub={<SourceChips row={open} />} onClose={close}>
              <SourcePanel key={open.key} row={open} canEdit={canEdit} isOffersOn={isOffersOn} changes={changes} />
            </SidePanel>
          )
        )
      }
    >
      <ListTable
        label="Sources"
        name={{ header: 'Source', label: (row) => row.label, render: (row) => <SourceName row={row} /> }}
        columns={columns}
        groups={[{ key: 'all', rows: shown }]}
        idOf={(row) => row.key}
        selectedId={open?.key ?? null}
        onOpen={(row) => setAddress({ open: row.key })}
        isMuted={(row) => row.isHidden}
        isPending={(row) => isPendingRow(row.key)}
        phoneLine={(row) => `${KIND_LABELS[row.kind]} · ${hiredHeader.toLowerCase()}: ${formatNumber(row.hired)}`}
        trailing={(row) =>
          canEdit &&
          row.isHidden && (
            <Button size="sm" variant="ghost" icon={Eye} onClick={() => setHidden(changes, row, false)}>
              Show
            </Button>
          )
        }
        empty={<ListNoMatch what="source" query={query} onClear={() => setAddress({ q: undefined, show: undefined })} />}
      />
    </SimpleListPage>
  );
}

function SourceName({ row }: { readonly row: AdminSourceView }): ReactNode {
  return (
    <>
      {row.label}
      {row.kind === 'custom' && <Chip plain>Added</Chip>}
      {row.isHidden && <ToneChip tone={ARCHIVED_TONE}>Hidden</ToneChip>}
    </>
  );
}

function SourceChips({ row }: { readonly row: AdminSourceView }): ReactNode {
  return (
    <>
      <Chip plain>{KIND_LABELS[row.kind]}</Chip>
      {row.isHidden && <ToneChip tone={ARCHIVED_TONE}>Hidden</ToneChip>}
    </>
  );
}

function SourcePanel({ row, canEdit, isOffersOn, changes }: { readonly row: AdminSourceView; readonly canEdit: boolean; readonly isOffersOn: boolean; readonly changes: RowChanges<AdminSourceView> }): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);
  const name = draft ?? row.label;
  const trimmed = name.trim();
  const isNameValid = trimmed.length >= 2 && trimmed.length <= 60;
  const isChanged = trimmed !== row.label;
  const isSystem = row.kind === 'system';

  const rename = (): void => {
    if (!isNameValid || !isChanged) return;
    const before = row.label;
    setDraft(null);
    changes.changeWithUndo(
      { id: row.key, patch: { label: trimmed }, send: sourcesApi.change(row.key, { label: trimmed }), failure: "The source wasn't renamed" },
      { message: `Renamed to “${trimmed}”`, undo: { id: row.key, patch: { label: before }, send: sourcesApi.change(row.key, { label: before }), failure: "The old name wasn't put back" } },
    );
  };

  const setPublishable = (isPublishable: boolean): void =>
    changes.changeWithUndo(
      { id: row.key, patch: { isPublishable }, send: sourcesApi.change(row.key, { isPublishable }), failure: "The source wasn't changed" },
      {
        message: isPublishable ? `Jobs can be published to “${row.label}”` : `Jobs can no longer be published to “${row.label}”`,
        undo: { id: row.key, patch: { isPublishable: !isPublishable }, send: sourcesApi.change(row.key, { isPublishable: !isPublishable }), failure: "The change wasn't put back" },
      },
    );

  return (
    <>
      <PanelSection>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            rename();
          }}
        >
          <Field label="Name" hint="Renaming changes the name everywhere, past applications included. Its key stays the same." error={trimmed.length > 0 && trimmed.length < 2 ? 'At least 2 characters.' : undefined}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={name} maxLength={60} disabled={!canEdit} onChange={(event) => setDraft(event.target.value)} />
            )}
          </Field>
        </form>
        {!isSystem && <Switch checked={row.isPublishable} disabled={!canEdit} label="Jobs can be published to it" onChange={setPublishable} />}
        <PanelFacts
          facts={[
            { label: 'Applications', value: formatNumber(row.applications) },
            { label: isOffersOn ? 'Hired from it' : 'Selected from it', value: formatNumber(row.hired) },
            { label: 'Key', value: <code>{row.key}</code> },
          ]}
        />
        {!isOffersOn && <p>Offers is switched off, so nobody is hired in the app: this counts everyone Selected who came from it.</p>}
      </PanelSection>
      <PanelSection>
        {isSystem ? (
          <p>The system sets this source itself and it is never picked by hand, so only its name can change.</p>
        ) : (
          <p>Hiding takes it out of the pickers. Past applications keep it, and source reports still count it.</p>
        )}
        {canEdit && (
          <PanelActions>
            {isSystem && <span />}
            {!isSystem && row.isHidden && (
              <Button variant="outline" size="sm" icon={Eye} onClick={() => setHidden(changes, row, false)}>
                Show in the lists
              </Button>
            )}
            {!isSystem && !row.isHidden && (
              <Button variant="ghost" size="sm" icon={EyeOff} onClick={() => setHidden(changes, row, true)}>
                Hide from the lists
              </Button>
            )}
            <Button variant="solid" size="sm" disabled={!isNameValid || !isChanged} onClick={rename}>
              Save name
            </Button>
          </PanelActions>
        )}
      </PanelSection>
    </>
  );
}

function AddSource({ changes, onAdded }: { readonly changes: RowChanges<AdminSourceView>; readonly onAdded: (key: string) => void }): ReactNode {
  const [label, setLabel] = useState('');
  const [isPublishable, setIsPublishable] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const add = async (): Promise<void> => {
    const parsed = createSourceSchema.safeParse({ label, isPublishable });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the name.');
      return;
    }
    const typed = parsed.data;
    setError(null);
    setLabel('');
    try {
      const saved = await changes.add(placeholderOf(typed.label, typed.isPublishable), async () => {
        const rows = await sourcesApi.create(typed);
        const row = rows.find((source) => source.key === customSourceKey(typed.label));
        if (!row) throw new ApiError({ code: 'not_found', message: 'The source was added, but the list did not come back with it. Reload to see it.', status: 404 });
        return row;
      });
      onAdded(saved.key);
    } catch (cause) {
      // A refusal keeps what was typed (review W20).
      setLabel(typed.label);
      setError(errorMessage(cause));
    }
  };

  return (
    <PanelSection>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <Field label="Name" error={error ?? undefined}>
          {({ inputId, describedBy, isInvalid }) => (
            <Input id={inputId} autoFocus aria-describedby={describedBy} isInvalid={isInvalid} value={label} maxLength={60} placeholder="e.g. Skill.jobs" onChange={(event) => setLabel(event.target.value)} />
          )}
        </Field>
      </form>
      <Switch checked={isPublishable} onChange={setIsPublishable} label="Jobs can be published to it" />
      <PanelActions>
        <span />
        <Button variant="solid" size="sm" icon={Plus} disabled={label.trim().length < 2} onClick={() => void add()}>
          Add
        </Button>
      </PanelActions>
    </PanelSection>
  );
}

function setHidden(changes: RowChanges<AdminSourceView>, row: AdminSourceView, isHidden: boolean): void {
  changes.changeWithUndo(
    { id: row.key, patch: { isHidden }, send: sourcesApi.change(row.key, { isHidden }), failure: isHidden ? "The source wasn't hidden" : "The source wasn't shown" },
    {
      message: isHidden ? `“${row.label}” hidden from the lists` : `“${row.label}” is back in the lists`,
      undo: { id: row.key, patch: { isHidden: !isHidden }, send: sourcesApi.change(row.key, { isHidden: !isHidden }), failure: "The change wasn't put back" },
    },
  );
}

function placeholderOf(label: string, isPublishable: boolean): AdminSourceView {
  return { key: pendingId(), label, kind: 'custom', isPublishable, isHidden: false, sortOrder: Number.MAX_SAFE_INTEGER, applications: 0, hired: 0, version: 0 };
}

/** "Job board": a system source is never picked, so the question doesn't apply. */
function boardText(row: AdminSourceView): string {
  if (row.kind === 'system') return '—';
  return row.isPublishable ? 'Yes' : 'No';
}

function matchesFilter(row: AdminSourceView, filter: SourceFilter | null): boolean {
  if (filter === 'shown') return !row.isHidden;
  if (filter === 'hidden') return row.isHidden;
  if (filter === 'added') return row.kind === 'custom';
  return true;
}

function matchesQuery(row: AdminSourceView, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || row.label.toLowerCase().includes(needle) || row.key.includes(needle);
}
